import { ref } from "vue";
import { defineStore } from "pinia";
import { useToasterStore } from "@comp-lib/stores";
import { isDns, isUrl } from "@core/validation";
import type { ImageData } from "@/components/ImageUploader.vue";
import { getPortalSettings, editPortalSettings, verifyPortalDomain } from "@/services/psaService";
import { useCurrentTenant } from "@/composables/useCurrentTenant";
import { DEFAULT_CURRENCY } from "@/assets/currencies";

export interface PortalBrandingPreferences {
  portalName: string;
  mainWebsiteUrl: string;
  logoUrl: string;
  portalDomain: string;
  defaultCurrency: string;
  portalDomainStatus: "" | "success" | "failure";
}

function emptyPreferences(): PortalBrandingPreferences {
  return {
    portalName: "",
    mainWebsiteUrl: "",
    logoUrl: "",
    portalDomain: "",
    defaultCurrency: DEFAULT_CURRENCY,
    portalDomainStatus: ""
  };
}

export const usePortalBrandingStore = defineStore("portalBranding", () => {
  const currentTenant = useCurrentTenant();

  const toasterStore = useToasterStore();
  const { triggerNotif } = toasterStore;

  const companyLogoImage = ref<ImageData | null>(null);
  const previewLogo = ref("");
  const verifiedPortalDomain = ref("");

  const loadingStates = ref({
    fetching: false,
    updating: false,
    verifyingDomain: false
  });

  const initialPreferences = ref<PortalBrandingPreferences>(emptyPreferences());
  const preferences = ref<PortalBrandingPreferences>(emptyPreferences());

  async function getPortalBrandingSettings(): Promise<void> {
    loadingStates.value.fetching = true;

    try {
      const settings = await getPortalSettings(currentTenant.value.id);

      preferences.value.portalName = settings.portal_name || currentTenant.value.name || "";
      preferences.value.mainWebsiteUrl = settings.main_website_url || "";
      preferences.value.logoUrl = settings.logo_url || "";
      preferences.value.portalDomain = settings.portal_domain || "";
      preferences.value.defaultCurrency = settings.default_currency || DEFAULT_CURRENCY;

      previewLogo.value = settings.logo_url || "";

      if (settings.portal_domain) {
        try {
          const isVerified = await verifyPortalDomain(currentTenant.value.id, settings.portal_domain);
          preferences.value.portalDomainStatus = isVerified ? "success" : "failure";
          verifiedPortalDomain.value = isVerified ? settings.portal_domain : "";
        } catch {
          console.log("Could not verify portal domain, treating as unverified");
          preferences.value.portalDomainStatus = "";
          verifiedPortalDomain.value = "";
        }
      }
    } catch {
      // Portal settings not configured yet — fall back to sensible defaults.
      console.log("Portal settings not found, using defaults");
      preferences.value = emptyPreferences();
      preferences.value.portalName = currentTenant.value.name || "";
    } finally {
      initialPreferences.value = JSON.parse(JSON.stringify(preferences.value));
      loadingStates.value.fetching = false;
    }
  }

  function uploadImage(imageData: ImageData | null) {
    companyLogoImage.value = imageData;
    previewLogo.value = imageData ? imageData.dataUrl : preferences.value.logoUrl;
  }

  function addHttpsToUrl(value: string) {
    let finalUrl: string | null = null;

    if (isDns(value)) finalUrl = `https://${value}`;

    try {
      const url = new URL(value);
      if (!/^https?:$/.test(url.protocol)) url.protocol = "https:";
      finalUrl = url.toString();
    } catch { finalUrl = null }

    if (finalUrl) preferences.value.mainWebsiteUrl = finalUrl;
  }

  function setDefaultPortalDomain(companyWebsite: string) {
    if (!isUrl(companyWebsite.trim())) return;

    const url = new URL(companyWebsite);
    const arr = url.hostname.split(".");
    if (arr[0] === "www") arr.shift();
    arr.splice(0, 0, "payments");
    preferences.value.portalDomain = arr.join(".");
    preferences.value.portalDomainStatus = verifiedPortalDomain.value === preferences.value.portalDomain ? "success" : "";
  }

  function checkDomainVerification() {
    if (!verifiedPortalDomain.value) return;
    preferences.value.portalDomainStatus =
      preferences.value.portalDomain === verifiedPortalDomain.value ? "success" : "";
  }

  async function verifyDomain(): Promise<void> {
    const domain = preferences.value.portalDomain;
    if (!domain) return;

    loadingStates.value.verifyingDomain = true;

    try {
      const isVerified = await verifyPortalDomain(currentTenant.value.id, domain);

      preferences.value.portalDomainStatus = isVerified ? "success" : "failure";
      verifiedPortalDomain.value = isVerified ? domain : "";

      triggerNotif({
        type: isVerified ? "success" : "error",
        title: isVerified ? "Portal domain verified" : "Portal domain verification failed",
        bodyText: isVerified
          ? undefined
          : `Please add a CNAME record for ${domain} and try again. DNS changes can take some time to propagate.`
      });
    } catch {
      preferences.value.portalDomainStatus = "failure";
      triggerNotif({
        type: "error",
        title: "Error verifying portal domain",
        bodyText: "An error occurred while verifying the domain. Please try again, or contact us for support."
      });
    } finally {
      loadingStates.value.verifyingDomain = false;
    }
  }

  async function updatePortalBranding(): Promise<void> {
    loadingStates.value.updating = true;

    try {
      const payload = {
        portal_domain: preferences.value.portalDomain,
        default_currency: preferences.value.defaultCurrency,
        portal_name: preferences.value.portalName,
        logo_url: previewLogo.value || "",
        main_website_url: preferences.value.mainWebsiteUrl
      };

      await editPortalSettings(currentTenant.value.id, payload);

      preferences.value.logoUrl = payload.logo_url;
      companyLogoImage.value = null;
      initialPreferences.value = JSON.parse(JSON.stringify(preferences.value));

      triggerNotif({
        type: "success",
        title: "Portal branding updated successfully"
      });
    } catch {
      triggerNotif({
        type: "error",
        title: "Error updating portal branding",
        bodyText: "An error occurred while updating your settings. Please try again, or contact us for support."
      });
    } finally {
      loadingStates.value.updating = false;
    }
  }

  return {
    companyLogoImage,
    previewLogo,
    verifiedPortalDomain,
    loadingStates,
    initialPreferences,
    preferences,
    getPortalBrandingSettings,
    uploadImage,
    addHttpsToUrl,
    setDefaultPortalDomain,
    checkDomainVerification,
    verifyDomain,
    updatePortalBranding
  };
});
